import {
  AgxButton,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { mapPaymentStatus } from './MarketingPackagePaymentStatusMapper';
import { formatCurrency } from 'utils/formatNumber';
import { getMarketingPackageTotal } from 'utils/marketingPackageUtil';
import './MarketingPackageCompletePanel.scss';
import { MarketingPackageProjection } from '@urbanx/agx.marketing.client';

interface Props {
  onBack: () => void;
  marketingPackage: MarketingPackageProjection;
  addressLineOne: string;
  addressLineTwo: string;
}

const MarketingPackageCompletePanel = ({
  marketingPackage,
  addressLineOne,
  addressLineTwo,
}: Props) => {
  const screenSize = useWindowSize();
  const isMobile = screenSize === Breakpoints.Mobile;

  const getFileLink = useGetFileLink();

  const downloadDocument = async () => {
    if (marketingPackage.document?.containerFilePath) {
      const fileLink = await getFileLink(marketingPackage.document?.containerFilePath);
      if (fileLink !== null) {
        openFileInNewTab(!isMobile, fileLink);
      }
    }
  };

  const marketingPackageTotal = getMarketingPackageTotal(marketingPackage);

  const marketingPaymentStatusComponent = (
    <div className="grid-wrapper">
      <AgxSpacer direction="horizontal" fill className="card-header" contentJustification="center">
        {mapPaymentStatus(marketingPackage.displayStatus)}
      </AgxSpacer>
      {marketingPackage.displayStatusExtraInfo && (
        <AgxSpacer
          direction="horizontal"
          fill
          className="payment-status-row"
          contentJustification="center"
        >
          <AgxTypography id="marketing-package-payment-status-extra-info" type="body" size="small">
            * {marketingPackage.displayStatusExtraInfo}
          </AgxTypography>
        </AgxSpacer>
      )}
    </div>
  );

  const marketingInclusionsComponent = (
    <div className="grid-wrapper">
      <AgxSpacer direction="horizontal" fill className="card-header">
        <AgxTypography
          id="marketing-package-inclusions-heading"
          type="body"
          size="medium"
          style={{ fontWeight: 400 }}
        >
          Inclusions
        </AgxTypography>
      </AgxSpacer>
      {marketingPackage?.inclusions?.map((inclusion, idx) => {
        return (
          <AgxSpacer
            key={`inclusion-${idx}`}
            direction="horizontal"
            fill
            className="inclusions-row"
          >
            <AgxSpacer direction="horizontal" contentAlignment="center" fill>
              <AgxTypography
                id={`marketing-package-inclusion-${idx}-name`}
                type="body"
                size="small"
                style={{ fontSize: 13, fontWeight: 400 }}
              >
                {inclusion.itemTemplateName}
              </AgxTypography>
            </AgxSpacer>
            <AgxTypography
              id={`marketing-package-inclusion-${idx}-price`}
              type="body_ui_strong"
              size="small"
              style={{ fontSize: 13 }}
            >
              {!inclusion.pricePerUnit ? 'Complimentary' : formatCurrency(inclusion.pricePerUnit)}
            </AgxTypography>
          </AgxSpacer>
        );
      })}
    </div>
  );

  const marketingAddonsComponent = (
    <div className="grid-wrapper">
      <AgxSpacer direction="horizontal" fill className="card-header">
        <AgxTypography
          id="marketing-package-upgrades-heading"
          type="body"
          size="medium"
          style={{ fontWeight: 400 }}
        >
          Upgrades
        </AgxTypography>
      </AgxSpacer>
      {marketingPackage?.addons?.map((addon, idx) => {
        return (
          <AgxSpacer key={`addon-${idx}`} direction="horizontal" fill className="addons-row">
            <AgxSpacer direction="horizontal" contentAlignment="center" fill>
              <AgxTypography
                id={`marketing-package-addon-${idx}-name`}
                type="body"
                size="small"
                style={{ fontSize: 13, fontWeight: 400 }}
              >
                {addon.itemTemplateName}
              </AgxTypography>
            </AgxSpacer>
            <AgxTypography
              id={`marketing-package-addon-${idx}-price`}
              type="body_ui_strong"
              size="small"
              style={{ fontSize: 13 }}
            >
              {!addon.pricePerUnit ? 'Complimentary' : formatCurrency(addon.pricePerUnit)}
            </AgxTypography>
          </AgxSpacer>
        );
      })}
    </div>
  );

  const noMarketingInclusionsComponent = (
    <div className="grid-wrapper">
      <AgxSpacer direction="horizontal" fill className="card-header">
        <AgxTypography
          id="marketing-package-missing-inclusions-heading"
          type="body"
          size="small"
          style={{ fontWeight: 400 }}
        >
          Inclusions
        </AgxTypography>
      </AgxSpacer>
      <AgxSpacer key="inclusions-missing" direction="horizontal" fill className="inclusions-row">
        <AgxSpacer direction="horizontal" contentAlignment="center" fill>
          <AgxTypography
            id="marketing-package-missing-inclusions"
            type="body"
            size="small"
            style={{ fontSize: 13, fontWeight: 400 }}
          >
            Inclusions list not available. Download to view.
          </AgxTypography>
        </AgxSpacer>
      </AgxSpacer>
    </div>
  );

  const marketingTotalComponent = (
    <div className="grid-wrapper">
      {!!marketingPackage.marketingContributions?.vendorUpfrontPayment &&
        (!!marketingPackage.marketingContributions?.agentContributionNoReimbursement ||
          !!marketingPackage.marketingContributions?.agentContributionToBeReimbursed) && (
          <AgxSpacer
            direction="horizontal"
            fill
            className="total-row"
            contentJustification="space-between"
          >
            <AgxTypography
              id="marketing-package-vendor-pay-upfront-label"
              type="body"
              size="small"
              style={{ fontSize: 13, fontWeight: 400 }}
            >
              Vendor pay upfront
            </AgxTypography>
            <AgxTypography id="marketing-package-vendor-pay-upfront-value" type="body" size="small">
              {formatCurrency(marketingPackage.marketingContributions?.vendorUpfrontPayment)}
            </AgxTypography>
          </AgxSpacer>
        )}
      {!!marketingPackage.marketingContributions?.agentContributionNoReimbursement && (
        <AgxSpacer
          direction="horizontal"
          fill
          className="total-row"
          contentJustification="space-between"
        >
          <AgxTypography
            id="marketing-package-agent-contribution-no-reimbursement-label"
            type="body"
            size="small"
            style={{ fontSize: 13, fontWeight: 400 }}
          >
            Agent contribution (no reimbursement)
          </AgxTypography>
          <AgxTypography
            id="marketing-package-agent-contribution-no-reimbursement-value"
            type="body"
            size="small"
          >
            {formatCurrency(
              marketingPackage.marketingContributions?.agentContributionNoReimbursement
            )}
          </AgxTypography>
        </AgxSpacer>
      )}
      {!!marketingPackage.marketingContributions?.agentContributionToBeReimbursed && (
        <AgxSpacer
          direction="horizontal"
          fill
          className="total-row"
          contentJustification="space-between"
        >
          <AgxTypography
            id="marketing-package-agent-contribution-to-be-reimbursed-label"
            type="body"
            size="small"
            style={{ fontSize: 13, fontWeight: 400 }}
          >
            Agent contribution (to be reimbursed)
          </AgxTypography>
          <AgxTypography
            id="marketing-package-agent-contribution-to-be-reimbursed-value"
            type="body"
            size="small"
          >
            {formatCurrency(
              marketingPackage.marketingContributions?.agentContributionToBeReimbursed
            )}
          </AgxTypography>
        </AgxSpacer>
      )}
      <AgxSpacer
        direction="horizontal"
        fill
        className="total-row"
        contentJustification="space-between"
      >
        <AgxTypography
          id="marketing-package-total-label"
          type="body"
          size="small"
          style={{ fontSize: 13, fontWeight: 400 }}
        >
          Total
        </AgxTypography>
        <AgxTypography
          id="marketing-package-total-value"
          type="body_ui_strong"
          size="small"
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          {formatCurrency(marketingPackageTotal)}
        </AgxTypography>
      </AgxSpacer>
    </div>
  );

  return (
    <>
      <AgxSpacer direction="vertical" gap="spacing-xl" contentAlignment="center">
        <AgxTypography
          id="marketing-package-complete-address"
          type="heading"
          size="2x-large"
          style={{ textAlign: 'center' }}
        >
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxTypography>
        <AgxTypography
          id="marketing-package-complete-package-name"
          type="body"
          size="large"
          style={{ textAlign: 'center' }}
        >
          {marketingPackage.packageTemplateName}
        </AgxTypography>
      </AgxSpacer>
      {marketingPackage?.displayStatus && (
        <AgxSpacer direction="vertical" fill>
          {marketingPaymentStatusComponent}
        </AgxSpacer>
      )}
      {marketingPackage?.inclusions && (
        <AgxSpacer direction="vertical" fill>
          {marketingInclusionsComponent}
        </AgxSpacer>
      )}
      {marketingPackage?.addons && (
        <AgxSpacer direction="vertical" fill>
          {marketingAddonsComponent}
        </AgxSpacer>
      )}
      {!marketingPackage?.addons?.length && !marketingPackage?.inclusions?.length && (
        <AgxSpacer direction="vertical" fill>
          {noMarketingInclusionsComponent}
        </AgxSpacer>
      )}
      <AgxSpacer direction="vertical" fill>
        {marketingTotalComponent}
      </AgxSpacer>

      <AgxSpacer direction="horizontal" fill contentJustification="center" gap="lg">
        <AgxButton
          id="marketing-package-download"
          text="Download"
          variant="hollow"
          size={isMobile ? 'medium' : 'large'}
          onClick={downloadDocument}
        />
      </AgxSpacer>
    </>
  );
};

export default MarketingPackageCompletePanel;
